import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseFieldControlProps, BaseFieldProps } from './types';
import {
  BaseFieldContext,
  resolveBaseFieldValidationResult,
  type BaseFieldControlValue,
} from './internal/baseFieldContext';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function mergeAriaIds(...values: Array<string | undefined>) {
  const ids = values.flatMap(
    (value) => value?.split(/\s+/).filter(Boolean) ?? [],
  );
  return ids.length ? Array.from(new Set(ids)).join(' ') : undefined;
}

function resolveOptionalId(
  value: string | undefined,
  fallback: string,
  name: string,
) {
  if (value === undefined) return fallback;
  if (!value.trim() || /\s/.test(value)) {
    throw new TypeError(
      `BaseField ${name} must be a non-empty ID without whitespace.`,
    );
  }
  return value;
}

export const BaseField = forwardRef<HTMLDivElement, BaseFieldProps>(
  function BaseField(
    {
      label,
      description,
      message,
      error,
      required = false,
      disabled = false,
      htmlFor,
      descriptionId,
      messageId,
      errorId,
      validate,
      validationMode = 'onBlur',
      children,
      appearance,
      className,
      classNames,
      style,
      ...fieldProps
    },
    ref,
  ) {
    for (const [name, value] of [
      ['required', required],
      ['disabled', disabled],
    ] as const) {
      if (typeof value !== 'boolean') {
        throw new TypeError(`BaseField ${name} must be a boolean.`);
      }
    }
    if (validate !== undefined && typeof validate !== 'function') {
      throw new TypeError('BaseField validate must be a function.');
    }
    if (validationMode !== 'onBlur' && validationMode !== 'onChange') {
      throw new TypeError(
        'BaseField validationMode must be "onBlur" or "onChange".',
      );
    }

    const generatedId = useId();
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const hasDescription = hasRenderableNode(description);
    const [internalError, setInternalError] = useState<string | null>(null);
    const registeredControlsRef = useRef(new Set<symbol>());
    const validateRef = useRef(validate);
    const validationModeRef = useRef(validationMode);
    const disabledRef = useRef(disabled);
    validateRef.current = validate;
    validationModeRef.current = validationMode;
    disabledRef.current = disabled;
    const hasValidator = validate !== undefined;

    useEffect(() => {
      if (!hasValidator || disabled) {
        setInternalError(null);
      }
    }, [disabled, hasValidator]);

    useEffect(() => {
      if (hasValidator && registeredControlsRef.current.size !== 1) {
        throw new TypeError(
          'BaseField with validate must contain exactly one compatible Base control.',
        );
      }
    }, [hasValidator]);

    const registerControl = useCallback((controlKey: symbol) => {
      const registeredControls = registeredControlsRef.current;
      if (
        validateRef.current !== undefined &&
        !registeredControls.has(controlKey) &&
        registeredControls.size > 0
      ) {
        throw new TypeError(
          'BaseField with validate supports exactly one compatible Base control. Use BaseFieldset for control groups.',
        );
      }
      registeredControls.add(controlKey);
      return () => {
        registeredControls.delete(controlKey);
      };
    }, []);

    const runValidation = useCallback(
      (mode: 'onBlur' | 'onChange', value: BaseFieldControlValue) => {
        const currentValidate = validateRef.current;
        if (
          disabledRef.current ||
          currentValidate === undefined ||
          validationModeRef.current !== mode
        ) {
          return;
        }

        const result = resolveBaseFieldValidationResult(currentValidate(value));
        if (result === null) {
          setInternalError(null);
          return;
        }
        setInternalError(result);
      },
      [],
    );

    const notifyBlur = useCallback(
      (value: BaseFieldControlValue) => runValidation('onBlur', value),
      [runValidation],
    );
    const notifyChange = useCallback(
      (value: BaseFieldControlValue) => runValidation('onChange', value),
      [runValidation],
    );

    const hasExternalError = hasRenderableNode(error);
    const resolvedError = hasExternalError ? error : internalError;
    const hasError = hasRenderableNode(resolvedError);
    const hasMessage = !hasError && hasRenderableNode(message);
    const resolvedMessage = hasError ? resolvedError : message;
    const resolvedControlId = htmlFor ?? `${generatedId}-control`;
    const resolvedDescriptionId = hasDescription
      ? resolveOptionalId(
          descriptionId,
          `${generatedId}-description`,
          'descriptionId',
        )
      : undefined;
    const resolvedMessageId = hasMessage
      ? resolveOptionalId(messageId, `${generatedId}-message`, 'messageId')
      : undefined;
    const resolvedErrorId = hasError
      ? resolveOptionalId(errorId, `${generatedId}-error`, 'errorId')
      : undefined;
    const controlProps: BaseFieldControlProps = {
      id: resolvedControlId,
      'aria-describedby': mergeAriaIds(
        resolvedDescriptionId,
        resolvedMessageId,
      ),
      'aria-errormessage': resolvedErrorId,
      'aria-invalid': hasError ? true : undefined,
      'aria-required': required ? true : undefined,
      required: required || undefined,
      disabled: disabled || undefined,
    };
    const renderedChildren =
      typeof children === 'function' ? children(controlProps) : children;
    const fieldContextValue = useMemo(
      () => ({
        invalid: hasError,
        registerControl,
        notifyBlur,
        notifyChange,
      }),
      [hasError, notifyBlur, notifyChange, registerControl],
    );

    return (
      <div
        {...componentAttributes}
        {...fieldProps}
        ref={ref}
        aria-disabled={disabled || undefined}
        data-monkeys-component="field"
        data-slot="root"
        data-required={required || undefined}
        data-disabled={disabled || undefined}
        data-invalid={hasError || undefined}
        className={cn(
          theme.slots.fieldRoot,
          disabled && theme.slots.controlDisabled,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.field,
          ...style,
        }}
      >
        {hasRenderableNode(label) ? (
          <div
            data-slot="label-row"
            className={cn(theme.slots.fieldLabelRow, classNames?.labelRow)}
          >
            <label
              htmlFor={typeof children === 'function' ? resolvedControlId : htmlFor}
              data-slot="label"
              className={cn(theme.slots.fieldLabel, classNames?.label)}
            >
              {label}
              {required ? (
                <span
                  aria-hidden="true"
                  data-slot="required-indicator"
                  className={cn(
                    theme.slots.fieldRequired,
                    classNames?.required,
                  )}
                >
                  {' '}
                  *
                </span>
              ) : null}
            </label>
          </div>
        ) : null}

        {hasDescription ? (
          <div
            id={resolvedDescriptionId}
            data-slot="description"
            className={cn(
              theme.slots.fieldDescription,
              classNames?.description,
            )}
          >
            {description}
          </div>
        ) : null}

        {hasRenderableNode(renderedChildren) ? (
          <div
            data-slot="control"
            className={cn(theme.slots.fieldControl, classNames?.control)}
            style={theme.styles.fieldControl}
          >
            <BaseFieldContext.Provider value={fieldContextValue}>
              {renderedChildren}
            </BaseFieldContext.Provider>
          </div>
        ) : null}

        {hasRenderableNode(resolvedMessage) ? (
          <div
            id={hasError ? resolvedErrorId : resolvedMessageId}
            role={hasError ? 'alert' : undefined}
            data-slot={hasError ? 'error' : 'message'}
            className={cn(
              theme.slots.fieldMessage,
              hasError && 'text-destructive-text',
              classNames?.message,
            )}
          >
            {resolvedMessage}
          </div>
        ) : null}
      </div>
    );
  },
);
